import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { createTenant } from "../api/client";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";

export function PageTenantNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const s = slug.trim();
    const n = name.trim();
    const d = description.trim();
    if (!s || !n) return;

    try {
      setLoading(true);
      setError(null);
      const created = await createTenant(
        { slug: s, name: n, description: d ? d : null },
        undefined,
      );
      await queryClient.refetchQueries({ queryKey: ["hierarchy", "full"] });
      navigate(`/tenants/${encodeURIComponent(created.slug)}/products`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create tenant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel
        title="Create Tenant"
        subtitle="Tenants are the top-level container for products and services."
        toolbar={
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" form="tenant-new-form" disabled={loading}>
              Create
            </Button>
          </Stack>
        }
      >
        {error ? <PageErrorState message={error} sx={{ mb: 1.5 }} /> : null}
        <Box
          id="tenant-new-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 720 }}
        >
          <TextField
            fullWidth
            size="small"
            label="Tenant slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g., dev"
            InputProps={{ className: "om-text-mono" }}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Tenant name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Development"
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this tenant for?"
            multiline
            minRows={2}
          />
        </Box>
      </Panel>

      <Alert severity="info">
        Tip: after creating a tenant, add a product, then services under that product.
      </Alert>
    </Box>
  );
}
